import React, { useContext } from 'react'
import styled from 'styled-components'
import produce from 'immer'

import {
  useUserGeneratePersonalTokenMutation,
  useGetPersonalAccessTokensQuery,
  GetPersonalAccessTokensDocument,
} from 'gqlTypes'

import Button from 'components/Button'
import Caption from 'components/Caption'
import { Flex } from 'components/Box'
import Loader from 'components/Loader'
import Panel from 'components/Panel'

import ModalContext from 'containers/ModalManager/ModalContext'
import ToastContext from 'containers/ToastManager/ToastContext'

import GeneratedTokenModal from './components/GeneratedTokenModal'
import TokensTable from './components/TokensTable'

const PanelTitle = styled(Panel.Title)`
  display: flex;
  justify-content: space-between;
`
/**
 * GenerateNewTokenButton
 *
 * This component is responsible for generating a new Personal Token.
 * Internally it handles all the needed logic to perform the request and show the response
 *
 * In case of success it opens the {@link GeneratedTokenModal} with the new token data.
 * In case of failure it shows a toast with the back-end error message.
 *
 * It's also self-contained, therefore doesn't need any external props to properly work
 */
const GenerateNewTokenButton = () => {
  const { showModal } = useContext(ModalContext)
  const { showToast } = useContext(ToastContext)

  const handleMutationComplete = ({ userGeneratePersonalToken }) => {
    const errors = userGeneratePersonalToken.errors ?? []
    if (errors.length > 0) {
      showToast(errors[0].message)
      return
    }

    const token = userGeneratePersonalToken.personalAccessToken.code

    showModal(GeneratedTokenModal, {
      token,
    })
  }

  const handleMutationUpdate = (cache, result) => {
    const newResult = result.data.userGeneratePersonalToken.personalAccessToken

    const currentCache = cache.readQuery({
      query: GetPersonalAccessTokensDocument,
    })

    const newCache = produce(currentCache, draftState => {
      draftState.me.personalAccessTokens.push(newResult)
    })

    cache.writeQuery({
      query: GetPersonalAccessTokensDocument,
      data: newCache,
    })
  }

  const [
    generatePersonalToken,
    { loading },
  ] = useUserGeneratePersonalTokenMutation({
    onCompleted: handleMutationComplete,
    update: handleMutationUpdate,
    onError: 'unsafe-throw-exception',
  })

  return (
    <Button
      variant="primary"
      loading={loading}
      disabled={loading}
      onClick={generatePersonalToken}
      small>
      Generate new API Key
    </Button>
  )
}

/**
 * PersonalTokens
 *
 * This component is responsible for mounting the Personal Tokens layout.
 * It's the main component and it should be connected to an Router route.
 *
 * It's also self-contained, therefore doesn't need any external props to properly work.
 */
const PersonalTokens = () => {
  const { data, loading } = useGetPersonalAccessTokensQuery()
  const tokens = data?.me?.personalAccessTokens ?? []

  return (
    <Panel>
      <PanelTitle extra={<GenerateNewTokenButton />}>API Keys</PanelTitle>
      <Panel.Body>
        <Caption size="small">
          <p>API keys can be used to access the Cloud API.</p>
        </Caption>
        <Flex justifyContent="center">
          {loading ? <Loader type="spinner" primary /> : null}

          {tokens.length > 0 ? <TokensTable items={tokens} /> : null}
        </Flex>
      </Panel.Body>
    </Panel>
  )
}

export default PersonalTokens
